import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Calendar,
  Target,
  Star,
  ExternalLink,
  Loader2,
  AlertCircle,
  Briefcase,
  Code,
} from 'lucide-react';
import {
  GlassCard,
  GlassBadge,
  GlassButton,
  ProgressBar,
  SectionReveal,
} from '@/design-system';
import type { RootState, AppDispatch } from '@/store';
import type { Feature, MilestoneType } from '@/types';
import { fetchEpicMilestones } from '@/store/slices/milestonesSlice';

interface FeatureTimelineTabProps {
  feature: Feature;
}

const FeatureTimelineTab: React.FC<FeatureTimelineTabProps> = ({ feature }) => {
  const dispatch = useDispatch<AppDispatch>();

  // Get parent EPIC ID
  const epicId = feature.epicId;

  // Selectors
  const milestones = useSelector((state: RootState) =>
    state.milestones.milestones.filter((m) => m.epicId === epicId)
  );
  const isLoading = useSelector((state: RootState) => state.milestones.isLoading);
  const error = useSelector((state: RootState) => state.milestones.error);

  // Fetch milestones for parent EPIC
  useEffect(() => {
    if (epicId) {
      dispatch(fetchEpicMilestones(epicId));
    }
  }, [dispatch, epicId]);

  // Get quarter info
  const currentQuarter = Math.ceil((new Date().getMonth() + 1) / 3);
  const currentYear = new Date().getFullYear();

  // Get milestone type icon and color
  const getMilestoneTypeInfo = (type: MilestoneType) => {
    if (type === 'BUSINESS') {
      return {
        icon: Briefcase,
        color: 'text-accent-emerald',
        bgColor: 'bg-accent-emerald/10',
      };
    }
    return {
      icon: Code,
      color: 'text-accent-blue',
      bgColor: 'bg-accent-blue/10',
    };
  };

  // Sort milestones by date
  const sortedMilestones = [...milestones].sort(
    (a, b) => new Date(a.targetDate).getTime() - new Date(b.targetDate).getTime()
  );

  return (
    <div className="space-y-6">
      {/* Feature Delivery Context */}
      <SectionReveal>
        <GlassCard variant="strong" padding="md">
          <div className="flex items-center gap-2 mb-4">
            <Target className="w-5 h-5 text-accent-violet" />
            <h3 className="text-lg font-medium text-foreground">Feature Delivery Context</h3>
          </div>

          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-accent-violet">
                Q{feature.targetQuarter}
              </div>
              <div className="text-xs text-muted-foreground/60">Target Quarter</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-accent-blue">
                {feature.estimatedTeamWeeks || 0}
              </div>
              <div className="text-xs text-muted-foreground/60">Estimated TWs</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-accent-cyan">
                {feature.epic?.name ? '1' : '0'}
              </div>
              <div className="text-xs text-muted-foreground/60">Parent EPIC</div>
            </div>
          </div>

          {/* Link to Parent EPIC */}
          {feature.epic && (
            <div className="p-3 rounded-lg bg-white/5 border border-white/10">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-muted-foreground/60">Parent EPIC</div>
                  <div className="font-medium text-foreground">{feature.epic.name}</div>
                </div>
                <GlassButton
                  variant="ghost"
                  size="sm"
                  rightIcon={<ExternalLink className="w-3.5 h-3.5" />}
                >
                  View EPIC
                </GlassButton>
              </div>
            </div>
          )}
        </GlassCard>
      </SectionReveal>

      {/* Quarterly Timeline Bar */}
      <SectionReveal delay={0.05}>
        <GlassCard variant="subtle" padding="md">
          <div className="flex items-center gap-2 mb-4">
            <Calendar className="w-5 h-5 text-accent-cyan" />
            <h3 className="text-sm font-semibold text-foreground">Quarterly Timeline</h3>
          </div>

          <div className="grid grid-cols-4 gap-2">
            {[1, 2, 3, 4].map((q) => {
              const isTargetQuarter = q === feature.targetQuarter;
              const isCurrentQuarter = q === currentQuarter;

              return (
                <div
                  key={q}
                  className={`
                    relative p-4 rounded-lg text-center transition-all
                    ${isTargetQuarter
                      ? 'bg-accent-violet/20 border-2 border-accent-violet'
                      : isCurrentQuarter
                        ? 'bg-accent-cyan/10 border border-accent-cyan/30'
                        : 'bg-white/5 border border-white/10'
                    }
                  `}
                >
                  <div className={`
                    text-lg font-bold
                    ${isTargetQuarter ? 'text-accent-violet' : isCurrentQuarter ? 'text-accent-cyan' : 'text-muted-foreground'}
                  `}>
                    Q{q}
                  </div>
                  <div className="text-xs text-muted-foreground/60">{currentYear}</div>

                  {isTargetQuarter && (
                    <div className="absolute -top-2 left-1/2 -translate-x-1/2">
                      <GlassBadge variant="info" size="sm">Target</GlassBadge>
                    </div>
                  )}
                  {isCurrentQuarter && !isTargetQuarter && (
                    <div className="absolute -bottom-2 left-1/2 -translate-x-1/2">
                      <GlassBadge variant="warning" size="sm">Current</GlassBadge>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </GlassCard>
      </SectionReveal>

      {/* Parent EPIC Milestones */}
      <SectionReveal delay={0.1}>
        <GlassCard variant="subtle" padding="md">
          <div className="flex items-center gap-2 mb-4">
            <Star className="w-5 h-5 text-accent-amber" />
            <h3 className="text-sm font-semibold text-foreground">Parent EPIC Milestones</h3>
          </div>

          {/* Loading state */}
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 text-accent-cyan animate-spin" />
              <span className="ml-3 text-muted-foreground">Loading milestones...</span>
            </div>
          )}

          {/* Error state */}
          {error && !isLoading && (
            <div className="flex flex-col items-center justify-center py-8">
              <AlertCircle className="w-8 h-8 text-accent-rose mb-2" />
              <p className="text-sm text-muted-foreground">{error}</p>
            </div>
          )}

          {/* Empty state */}
          {!isLoading && !error && sortedMilestones.length === 0 && (
            <div className="text-center py-8">
              <div className="p-3 rounded-xl bg-gradient-to-br from-accent-amber/10 to-accent-amber/5 w-fit mx-auto mb-4">
                <Star className="w-8 h-8 text-accent-amber/50" />
              </div>
              <h4 className="text-base font-medium text-muted-foreground mb-1">No Milestones</h4>
              <p className="text-sm text-muted-foreground/60">
                {epicId
                  ? 'The parent EPIC has no milestones defined yet.'
                  : 'This feature is not linked to an EPIC.'}
              </p>
            </div>
          )}

          {/* Milestones list */}
          {!isLoading && !error && sortedMilestones.length > 0 && (
            <div className="space-y-3">
              {sortedMilestones.map((milestone) => {
                const typeInfo = getMilestoneTypeInfo(milestone.type);
                const TypeIcon = typeInfo.icon;
                const targetDate = new Date(milestone.targetDate);
                const isPast = targetDate < new Date();

                return (
                  <div
                    key={milestone.id}
                    className="flex items-start gap-3 p-3 rounded-lg bg-white/5 border border-white/10 hover:border-white/20 transition-colors"
                  >
                    <div className={`p-2 rounded-lg ${typeInfo.bgColor}`}>
                      <TypeIcon className={`w-4 h-4 ${typeInfo.color}`} />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-foreground truncate">
                          {milestone.description || 'Milestone'}
                        </span>
                        <GlassBadge
                          variant={milestone.type === 'BUSINESS' ? 'success' : 'info'}
                          size="sm"
                        >
                          {milestone.type}
                        </GlassBadge>
                      </div>

                      <div className="flex items-center gap-4 mt-2">
                        <div className="text-xs text-muted-foreground/60">
                          <Calendar className="w-3.5 h-3.5 inline-block mr-1" />
                          {targetDate.toLocaleDateString()}
                          {isPast && (
                            <span className="ml-1 text-accent-amber">(Past)</span>
                          )}
                        </div>
                      </div>

                      {/* Completion progress */}
                      <div className="mt-2">
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="text-muted-foreground/60">Completion</span>
                          <span className={
                            milestone.completionPercentage >= 100 ? 'text-accent-emerald' :
                            milestone.completionPercentage >= 50 ? 'text-accent-amber' : 'text-muted-foreground'
                          }>
                            {milestone.completionPercentage}%
                          </span>
                        </div>
                        <ProgressBar
                          value={milestone.completionPercentage}
                          color={
                            milestone.completionPercentage >= 100 ? 'emerald' :
                            milestone.completionPercentage >= 50 ? 'amber' : 'rose'
                          }
                          size="sm"
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </GlassCard>
      </SectionReveal>
    </div>
  );
};

export default FeatureTimelineTab;
